import styled from '@emotion/styled'
import { useHover } from '@react-aria/interactions'
import React from 'react'

import { Tooltip } from 'layouts/components/SharedStyledComponents'
import { RuleCondition } from 'types/rules'

const TooltipWrapper = styled.div`
	position: relative;
	display: flex;
`

const StyledTooltip = styled(Tooltip)<{ tooltipPosition: { top: number; left: number } }>`
	min-width: 160px;
	max-width: 220px;
	padding: ${({ theme }) => theme.spaces.space4};
	position: fixed;
	top: ${({ tooltipPosition }) => `${tooltipPosition.top}px`};
	left: ${({ tooltipPosition }) => `${tooltipPosition.left}px`};
	${({ theme }) => theme.typography.caption};
	color: ${({ theme }) => theme.colors.light.grayscale[100]};
	z-index: ${({ theme }) => theme.zIndexes.popover};
	transition: 0.3s ${({ theme }) => theme.transitions.easings.motion.smooth};
	display: flex;
	flex-direction: column;
	gap: ${({ theme }) => theme.spaces.space1};
`

const RemainingCondition = styled.span`
	text-wrap: nowrap;
	overflow-x: hidden;
	text-overflow: ellipsis;
`

type RuleConditionTooltipProps = {
	children: React.ReactNode
	remainingConditions: RuleCondition[]
}

function RuleConditionTooltip({ children, remainingConditions }: RuleConditionTooltipProps) {
	const tooltipRef = React.useRef<HTMLDivElement>(null)
	const wrapperRef = React.useRef<HTMLDivElement>(null)
	const { hoverProps, isHovered } = useHover({})
	const [tooltipPosition, setTooltipPosition] = React.useState({ top: 0, left: 0 })

	React.useEffect(() => {
		if (isHovered && wrapperRef.current) {
			const rect = wrapperRef.current.getBoundingClientRect()

			setTooltipPosition({
				top: rect.top + window.scrollY,
				left: rect.left + 30,
			})
		}
	}, [isHovered])

	return (
		<TooltipWrapper ref={wrapperRef} {...hoverProps}>
			{isHovered && (
				<StyledTooltip
					ref={tooltipRef}
					tooltipPosition={tooltipPosition}
					initial={{ opacity: 0 }}
					animate={{ opacity: 1 }}
					exit={{ opacity: 0 }}
				>
					{remainingConditions.map((condition) => (
						<RemainingCondition key={condition.id}>
							{condition.matcher} {condition.pattern}
						</RemainingCondition>
					))}
				</StyledTooltip>
			)}
			{children}
		</TooltipWrapper>
	)
}

export default RuleConditionTooltip
